import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { FiCheckCircle, FiFolder, FiDollarSign } from 'react-icons/fi';
import './ClientPortal.css';

const formatMoney = (amount, currency = 'USD') => {
  const v = Number(amount || 0);
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency,
      maximumFractionDigits: 2,
    }).format(v);
  } catch (_e) {
    return `${v.toFixed(2)} ${currency}`;
  }
};

// Expandable task cost row with per-task payment history
// Phase 4 / Part I: when the task currency differs from the project base
// currency, render "100,000 TRY ≈ 3,125 USD" instead of an ambiguous total.
const ClientTaskCostRow = ({ task, baseCcy, visible }) => {
  const [expanded, setExpanded] = useState(false);
  const value = (visible ? task.valueBase : task.displayValueBase) || 0;
  const payments = task.payments || [];
  const paid = task.paid || 0;
  const remaining = Math.max(0, (task.value || 0) - paid);
  const hasPayments = payments.length > 0;
  const taskCurrency = (task.currency || baseCcy || 'USD').toUpperCase();
  const showOriginalBesideBase =
    taskCurrency !== (baseCcy || 'USD').toUpperCase() &&
    (task.originalAmount != null) && (task.originalAmount > 0);

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: 'white', borderRadius: 6, fontSize: 13, cursor: hasPayments ? 'pointer' : 'default' }}
           onClick={() => hasPayments && setExpanded(!expanded)}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          {hasPayments && (
            <span style={{ fontSize: 10, color: 'var(--monday-blue)', userSelect: 'none' }}>{expanded ? '▼' : '▶'}</span>
          )}
          {!hasPayments && <span style={{ width: 10, display: 'inline-block' }} />}
          <span>{task.name}</span>
        </span>
        <span style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
          {showOriginalBesideBase ? (
            <span style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
              <strong>{formatMoney(task.originalAmount, taskCurrency)}</strong>
              <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                ≈ {formatMoney(value, baseCcy)}
              </span>
            </span>
          ) : (
            <strong>{formatMoney(value, baseCcy)}</strong>
          )}
          {hasPayments && (
            <span style={{ fontSize: 10, color: 'var(--text-secondary)' }}>
              Paid {formatMoney(paid, task.currency)} of {formatMoney(task.value, task.currency)}
            </span>
          )}
        </span>
      </div>
      {expanded && hasPayments && (
        <div style={{ marginLeft: 16, marginTop: 4, marginBottom: 4, display: 'flex', flexDirection: 'column', gap: 3 }}>
          {payments.map((p, idx) => {
            const pCcy = (p.currency || task.currency || baseCcy || 'USD').toUpperCase();
            const showOrigP = pCcy !== (baseCcy || 'USD').toUpperCase() && (p.baseAmount != null);
            return (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 10px', background: 'var(--gray-50)', borderRadius: 4, fontSize: 12 }}>
                <span style={{ color: 'var(--text-secondary)' }}>
                  {p.date}{p.note ? ` — ${p.note}` : ''}
                </span>
                <strong>
                  {formatMoney(p.amount, pCcy)}
                  {showOrigP && (
                    <span style={{ fontWeight: 400, color: 'var(--text-secondary)', marginLeft: 6 }}>
                      ≈ {formatMoney(p.baseAmount, baseCcy)}
                    </span>
                  )}
                </strong>
              </div>
            );
          })}
          <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 10px', fontSize: 12, color: remaining > 0 ? '#E24B5B' : '#1DBF73' }}>
            <em>Remaining</em>
            <strong>{formatMoney(remaining, task.currency)}</strong>
          </div>
        </div>
      )}
    </div>
  );
};

const ClientPortal = () => {
  const { token } = useParams();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchClientPortal();
  }, [token]);

  const fetchClientPortal = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/client-portal/${token}`);
      if (response.ok) {
        const data = await response.json();
        setProjects(data.projects || []);
      }
    } catch (error) {
      console.error('Failed to fetch client portal:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      'Not Started': '#B9B7AE',
      'In Progress': '#0F0F10',
      'Done': '#1DBF73',
      'Blocked': '#E24B5B'
    };
    return colors[status] || '#B9B7AE';
  };

  if (loading) {
    return (
      <div className="client-portal-loading">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="client-portal">
      <div className="client-portal-header">
        <h1 className="client-portal-title">AA Project Management - Client Portal</h1>
        <p className="client-portal-subtitle">View your project progress</p>
      </div>

      <div className="client-projects">
        {projects.map((project) => (
          <div key={project.id} className="client-project-card">
            <div className="client-project-header">
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <div style={{ 
                  width: '48px', 
                  height: '48px', 
                  borderRadius: '8px', 
                  backgroundColor: `${project.color || '#0F0F10'}20`,
                  color: project.color || '#0F0F10',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <FiFolder size={24} />
                </div>
                <div>
                  <h2 className="client-project-name">{project.name}</h2>
                  {project.client && <div className="client-project-client">{project.client}</div>}
                </div>
              </div>
              <span className="client-project-status">Active</span>
            </div>
            
            {project.description && (
              <div className="client-project-desc">{project.description}</div>
            )}

            <div className="client-project-progress">
              <div className="progress-header">
                <span>Overall Progress</span>
                <span className="progress-value">{project.progress || 0}%</span>
              </div>
              <div className="progress-bar-container">
                <div
                  className="progress-bar-fill"
                  style={{ width: `${project.progress || 0}%` }}
                ></div>
              </div>
            </div>

            <div className="client-project-stats">
              <div className="client-stat">
                <div className="client-stat-label">Total Tasks</div>
                <div className="client-stat-value">{project.totalTasks || 0}</div>
              </div>
              <div className="client-stat">
                <div className="client-stat-label">Completed</div>
                <div className="client-stat-value" style={{ color: '#1DBF73' }}>
                  {project.completedTasks || 0}
                </div>
              </div>
            </div>

            {/* Finance Summary */}
            {project.finance && (project.finance.projectCostBase > 0 || project.finance.totalReceivedBase > 0) && (
              <div className="client-finance" data-testid="client-finance">
                <h3 className="client-finance-title">
                  <FiDollarSign size={18} />
                  <span>Financial Summary</span>
                </h3>

                {/* Phase 4 / Part J: warn when not all tasks are priced */}
                {project.finance.summaryWarning && (
                  <div
                    data-testid="client-finance-warning"
                    style={{
                      padding: '10px 14px',
                      marginBottom: 12,
                      background: 'rgba(244, 163, 64, 0.08)',
                      border: '1px solid rgba(244, 163, 64, 0.35)',
                      borderRadius: 8,
                      color: '#9a5a00',
                      fontSize: 13,
                      lineHeight: 1.5,
                    }}
                  >
                    <strong>Heads up — summary is not final.</strong><br/>
                    {project.finance.summaryWarning}
                  </div>
                )}
                <div className="client-finance-kpis">
                  <div className="client-finance-kpi">
                    <div className="client-finance-kpi-label">Project Cost</div>
                    <div className="client-finance-kpi-value">
                      {formatMoney(project.finance.projectCostBase, project.finance.baseCurrency || 'USD')}
                    </div>
                    {project.finance.supervisionFeePercent > 0 && project.finance.supervisionFeeVisible !== false && (
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                        incl. {project.finance.supervisionFeePercent}% supervision
                      </div>
                    )}
                  </div>
                  <div className="client-finance-kpi">
                    <div className="client-finance-kpi-label">Paid</div>
                    <div className="client-finance-kpi-value" style={{ color: '#1DBF73' }}>
                      {formatMoney(project.finance.totalReceivedBase, project.finance.baseCurrency || 'USD')}
                    </div>
                  </div>
                  <div className="client-finance-kpi">
                    <div className="client-finance-kpi-label">Remaining</div>
                    <div className="client-finance-kpi-value" style={{ color: project.finance.remainingBase > 0 ? '#E24B5B' : '#1DBF73' }}>
                      {formatMoney(project.finance.remainingBase, project.finance.baseCurrency || 'USD')}
                    </div>
                  </div>
                </div>
                <div className="client-finance-progress-track">
                  <div className="client-finance-progress-fill" style={{ width: `${Math.min(100, project.finance.percentPaid || 0)}%` }} />
                  <span className="client-finance-progress-text">{(project.finance.percentPaid || 0).toFixed(0)}% paid</span>
                </div>
                {project.finance.payments && project.finance.payments.length > 0 && (
                  <div style={{ marginTop: 12 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                      Payment History ({project.finance.payments.length})
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {project.finance.payments.map((p) => (
                        <div key={p.id} style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'white', borderRadius: 6, fontSize: 13 }}>
                          <span><strong>{formatMoney(p.amount, p.currency)}</strong>{p.note ? ` — ${p.note}` : ''}</span>
                          <span style={{ color: 'var(--text-secondary)' }}>{p.date}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cost Breakdown by Task */}
                {(() => {
                  const tasksWithCost = [];
                  (project.groups || []).forEach((g) => {
                    (g.items || []).forEach((it) => {
                      if (it.displayValue > 0 || it.value > 0) tasksWithCost.push({ ...it, groupName: g.name });
                    });
                  });
                  if (tasksWithCost.length === 0) return null;
                  const baseCcy = project.finance.baseCurrency || 'USD';
                  const visible = project.finance.supervisionFeeVisible !== false;
                  const supPct = project.finance.supervisionFeePercent || 0;
                  return (
                    <div style={{ marginTop: 16 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                        Cost Breakdown
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {tasksWithCost.map((it) => (
                          <ClientTaskCostRow key={it.id} task={it} baseCcy={baseCcy} visible={visible} />
                        ))}
                        {visible && supPct > 0 && (
                          <>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', borderTop: '1px solid var(--border-color, #e2e8f0)', marginTop: 6, fontSize: 13, fontWeight: 600 }}>
                              <span>Subtotal (tasks)</span>
                              <span>{formatMoney((project.finance.projectCostBase || 0) - (project.finance.supervisionFeeBase || 0), baseCcy)}</span>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 10px', background: 'rgba(0,115,234,0.06)', borderRadius: 6, fontSize: 13, fontStyle: 'italic' }}>
                              <span>Supervision Fee ({supPct}%)</span>
                              <span><strong>+ {formatMoney(project.finance.supervisionFeeBase || 0, baseCcy)}</strong></span>
                            </div>
                          </>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 10px', background: 'var(--gray-50)', borderRadius: 6, fontSize: 14, borderTop: '2px solid var(--monday-blue)', marginTop: 4 }}>
                          <strong>Total Project Cost</strong>
                          <strong style={{ color: 'var(--monday-blue)' }}>{formatMoney(project.finance.projectCostBase, baseCcy)}</strong>
                        </div>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Groups and Tasks Detail */}
            <div className="client-project-groups">
              <h3 className="section-title" style={{ fontSize: '18px', marginBottom: '16px' }}>Project Tasks</h3>
              {(project.groups || []).map((group, idx) => (
                <div key={group.id} className="client-group" style={{ marginBottom: '24px' }}>
                  <div className="client-group-header">
                    <div style={{ 
                      width: '4px', 
                      height: '20px', 
                      backgroundColor: group.color || '#0F0F10', 
                      borderRadius: '2px',
                      marginRight: '12px'
                    }}></div>
                    <h4 className="client-group-name">{group.name}</h4>
                    <span className="client-group-count">({group.items?.length || 0} tasks)</span>
                  </div>
                  
                  <div className="client-tasks-list">
                    {(group.items || []).map((item) => (
                      <div key={item.id} className="client-task-item">
                        <div className="client-task-content">
                          <div className="client-task-name">{item.name}</div>
                          {item.dueDate && (
                            <div className="client-task-due">Due: {new Date(item.dueDate).toLocaleDateString()}</div>
                          )}
                          {Array.isArray(item.subtasks) && item.subtasks.length > 0 && (
                            <ul className="client-subtasks-list" data-testid={`client-subtasks-${item.id}`}>
                              {item.subtasks.map((sub, sIdx) => (
                                <li key={sIdx} className="client-subtask-row">
                                  <span
                                    className="client-subtask-dot"
                                    style={{ backgroundColor: getStatusColor(sub.status) }}
                                    aria-hidden="true"
                                  ></span>
                                  <span className="client-subtask-name">{sub.name}</span>
                                  <span
                                    className="client-subtask-status"
                                    style={{ color: getStatusColor(sub.status) }}
                                  >
                                    {sub.status}
                                  </span>
                                  {typeof sub.progress === 'number' && sub.progress > 0 && (
                                    <span className="client-subtask-progress">{sub.progress}%</span>
                                  )}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          {item.progress !== undefined && (
                            <div className="client-task-progress">
                              <div className="mini-progress-bar">
                                <div 
                                  className="mini-progress-fill" 
                                  style={{ 
                                    width: `${item.progress}%`,
                                    backgroundColor: getStatusColor(item.status)
                                  }}
                                ></div>
                              </div>
                              <span className="progress-label">{item.progress}%</span>
                            </div>
                          )}
                          <span 
                            className="client-status-pill"
                            style={{ backgroundColor: getStatusColor(item.status) }}
                          >
                            {item.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {project.approvedDocs && project.approvedDocs.length > 0 && (
              <div className="client-project-docs">
                <h3 className="docs-title">Approved Documents</h3>
                <div className="docs-list">
                  {project.approvedDocs.map((doc, idx) => (
                    <a
                      key={idx}
                      href={doc.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="doc-link"
                    >
                      <FiCheckCircle size={16} style={{ color: '#1DBF73' }} />
                      <span>{doc.name}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default ClientPortal;